import React from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    Box,
    Typography,
    LinearProgress,
} from '@mui/material';
import { t } from 'i18next';

import useAssociatedApplicants from './useAssociatedApplicants';
import RenderIf from '../../renderIf';
import DateTimeText from '../../shared/DateTimeText';
import useStyles from './styles';
import useDebounce from '../../../utils/useDebounce';
import TableSkeletons from './tableSkeletons';

const AssociatedApplicants = (props) => {
    const { UserID } = props;

    const classes = useStyles();
    const tableEl = React.useRef();

    const { associatedApplicants, retrieveAssociatedApplicants, sortAssociatedApplicants, loadMore } = useAssociatedApplicants(UserID);
    const [distanceBottom, setDistanceBottom] = React.useState(0);

    const debouncedLoadMore = useDebounce(loadMore, 200);

    const scrollListener = React.useCallback(() => {
        let bottom = tableEl.current.scrollHeight - tableEl.current.clientHeight;
        // if you want to change distanceBottom every time new data is loaded
        // don't use the if statement
        if (!distanceBottom) {
            // calculate distanceBottom that works for you
            setDistanceBottom(Math.round(bottom * 0.8));
        }
        if (tableEl.current.scrollTop > bottom - distanceBottom && associatedApplicants.hasMore && !associatedApplicants.isFetching) {
            loadMore();
        }
    }, [associatedApplicants.hasMore, debouncedLoadMore, associatedApplicants.isFetching, distanceBottom]);

    React.useEffect(() => {
        retrieveAssociatedApplicants();
    }, []);

    React.useLayoutEffect(() => {
        const tableRef = tableEl.current;
        tableRef?.addEventListener?.('scroll', scrollListener);
        return () => {
            tableRef?.removeEventListener?.('scroll', scrollListener);
        };
    }, [scrollListener]);

    return (
        <Box className={classes.container}>
            <TableContainer
                ref={tableEl}
                style={{ maxHeight: '40rem' }}
            >
                <RenderIf isTrue={associatedApplicants?.headers instanceof Array}>
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow>
                                {associatedApplicants?.headers?.map(([key, value]) => (
                                    <TableCell key={`header-${key}`}>
                                        <TableSortLabel
                                            active={associatedApplicants?.sortField === key}
                                            direction={associatedApplicants?.sortDir.toLowerCase()}
                                            onClick={() => sortAssociatedApplicants(key)}
                                            sx={{ fontWeight: 700, color: 'grey.header', fontSize: '13px' }}
                                        >
                                            {t(value)}
                                        </TableSortLabel>
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            <RenderIf isTrue={associatedApplicants?.associations?.length > 0}>
                                {associatedApplicants?.associations?.map((item, i) => (
                                    <TableRow
                                        key={`row-${i}`}
                                        style={{ backgroundColor: i % 2 === 1 ? '#F9FBFF' : null }}
                                    >
                                        {associatedApplicants?.headers?.map(([key], j) => {
                                            switch (key) {
                                                case 'DOB':
                                                    return (
                                                        <TableCell key={`cell-${i}-${j}`}>
                                                            <RenderIf isTrue={item[key] !== null}>
                                                                <Typography
                                                                    sx={{ fontSize: '13px', color: 'grey.field-text' }}
                                                                    component="div"
                                                                >
                                                                    <DateTimeText
                                                                        type="short"
                                                                        date={item[key]}
                                                                    />
                                                                </Typography>
                                                            </RenderIf>
                                                        </TableCell>
                                                    );
                                                case 'Terms Description':
                                                    return (
                                                        <TableCell key={`cell-${i}-${j}`}>
                                                            {item[key].split('|').map((term, k) => (
                                                                <Typography
                                                                    key={`term-${k}`}
                                                                    sx={{ fontSize: '13px', color: 'grey.field-text' }}
                                                                >
                                                                    {term}
                                                                </Typography>
                                                            ))}
                                                        </TableCell>
                                                    );
                                                default:
                                                    return (
                                                        <TableCell key={`cell-${i}-${j}`}>
                                                            <Typography sx={{ fontSize: '13px', color: 'grey.field-text' }}>
                                                                {item[key]}
                                                            </Typography>
                                                        </TableCell>
                                                    );
                                            }
                                        })}
                                    </TableRow>
                                ))}
                            </RenderIf>

                            <RenderIf isTrue={!associatedApplicants?.isFetching && associatedApplicants?.associations?.length === 0}>
                                <TableRow>
                                    <TableCell
                                        colSpan={associatedApplicants?.headers?.length}
                                        align="center"
                                    >
                                        <Typography>{t('Associated.NoResults')}</Typography>
                                    </TableCell>
                                </TableRow>
                            </RenderIf>

                            <RenderIf isTrue={associatedApplicants.isFetching}>
                                <TableSkeletons headerLength={associatedApplicants.headers?.length} />
                            </RenderIf>
                        </TableBody>
                    </Table>
                </RenderIf>

                <RenderIf isTrue={associatedApplicants?.isFetching && !(associatedApplicants?.headers instanceof Array)}>
                    <Box sx={{ width: '100%' }}>
                        <LinearProgress
                            color="grey"
                            sx={{ height: 10, borderRadius: 5 }}
                        />
                    </Box>
                </RenderIf>
            </TableContainer>
        </Box>
    );
};

export default AssociatedApplicants;
